import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingCart, Trash2, Plus, Minus, CreditCard, Package, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, removeFromCart, checkout, loading, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Inicia sesión para continuar");
      navigate("/login");
      setCartOpen(false);
      return;
    }
    try {
      const result = await checkout();
      if (result?.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      toast.error("Error al procesar el pago. Intenta de nuevo.");
    }
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={() => setCartOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div className="flex items-center gap-3">
                <ShoppingCart size={22} className="text-[#5D4037]" />
                <h2 className="text-lg font-bold text-[#5D4037]">Mi Carrito</h2>
                <span className="bg-[#F57C00] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {cart.items.length}
                </span>
              </div>
              <button onClick={() => setCartOpen(false)} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="close-cart-btn">
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-5">
              {cart.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Package size={48} className="text-stone-300 mb-4" />
                  <p className="text-stone-500 font-medium">Tu carrito está vacío</p>
                  <p className="text-stone-400 text-sm mt-1">Explora nuestra tienda para agregar productos</p>
                  <button
                    onClick={() => { setCartOpen(false); navigate("/tienda"); }}
                    className="btn-primary mt-6 text-sm"
                    data-testid="go-to-store-btn"
                  >
                    Ir a la Tienda
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.items.map((item, idx) => (
                    <div key={`${item.product_id}-${item.pack_type}`} className="flex gap-4 p-3 rounded-xl bg-stone-50 border border-stone-100" data-testid={`cart-item-${idx}`}>
                      {item.image && (
                        <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#5D4037] text-sm truncate">{item.name}</p>
                        <p className="text-xs text-stone-400 mt-0.5">
                          {item.pack_type === "pack" ? `Econo-Pak (${item.pack_qty} pzas)` : "Individual"}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-bold text-[#F57C00] text-sm">
                            ${item.line_total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-stone-500 mr-1">×{item.quantity}</span>
                            <button
                              onClick={() => removeFromCart(item.product_id, item.pack_type)}
                              className="p-1 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded"
                              data-testid={`remove-item-${idx}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {cart.items.length > 0 && (
              <div className="border-t border-stone-100 p-5 space-y-3 bg-stone-50/50">
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-stone-600">
                    <span>Subtotal</span>
                    <span>${cart.subtotal?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>IVA (16%)</span>
                    <span>${cart.iva?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between font-bold text-[#5D4037] text-lg pt-2 border-t border-stone-200">
                    <span>Total</span>
                    <span>${cart.total?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2 text-center"
                  data-testid="checkout-btn"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  {loading ? "Procesando..." : "Pagar con Stripe"}
                </button>
                <button
                  onClick={clearCart}
                  className="w-full text-sm text-stone-400 hover:text-red-500 transition-colors py-1"
                  data-testid="clear-cart-btn"
                >
                  Vaciar carrito
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
